// Level-up draft popup. Pauses behind it (orchestrated in main). Select an upgrade,
// then confirm it explicitly so a stray touch cannot commit the wrong card.

import type { DraftChoice } from '../game/upgrades';
import { adaptiveHint, A_BTN } from './hint';
import { looksXbox, setInputMode } from '../core/inputMode';

const hex = (n: number): string => '#' + n.toString(16).padStart(6, '0');

export class DraftUI {
  private readonly overlay: HTMLDivElement;
  private readonly row: HTMLDivElement;
  private readonly confirmButton: HTMLButtonElement;
  private onPick: ((c: DraftChoice) => void) | null = null;
  private choices: DraftChoice[] = [];
  private cards: HTMLDivElement[] = [];
  private selected = 0;
  private padRaf = 0;
  private readonly prevPad = { left: false, right: false, a: false };

  constructor(mount: HTMLElement) {
    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'absolute',
      inset: '0',
      display: 'none',
      placeItems: 'center',
      background: 'rgba(8,6,14,0.78)',
      zIndex: '10',
      userSelect: 'none',
      font: '15px "Silkscreen", ui-monospace, monospace',
      color: '#e8e4f0',
    });

    const panel = document.createElement('div');
    Object.assign(panel.style, { textAlign: 'center', maxWidth: '760px', padding: '16px' });

    const title = document.createElement('div');
    title.textContent = 'LEVEL UP - choose an upgrade';
    Object.assign(title.style, { fontSize: '20px', marginBottom: '16px', letterSpacing: '1px' });

    this.row = document.createElement('div');
    Object.assign(this.row.style, {
      display: 'flex',
      gap: '14px',
      justifyContent: 'center',
      flexWrap: 'wrap',
    });

    this.confirmButton = document.createElement('button');
    this.confirmButton.type = 'button';
    this.confirmButton.textContent = 'CONFIRM UPGRADE';
    Object.assign(this.confirmButton.style, {
      minWidth: '220px',
      minHeight: '48px',
      marginTop: '16px',
      padding: '10px 20px',
      border: '2px solid #6b4a08',
      borderRadius: '6px',
      background: '#f0a81e',
      color: '#2a1a00',
      font: '700 15px "Silkscreen", ui-monospace, monospace',
      cursor: 'pointer',
      boxShadow: '0 4px 0 #6b4a08',
      touchAction: 'manipulation',
    });
    this.confirmButton.onclick = (): void => this.confirm();

    const hint = adaptiveHint('←  → / 1 2 3 select    ·    ENTER confirm', `D-pad select    ·    ${A_BTN} confirm`);
    Object.assign(hint.style, { marginTop: '18px', fontSize: '12px', opacity: '0.7', letterSpacing: '1px' });

    panel.appendChild(title);
    panel.appendChild(this.row);
    panel.appendChild(this.confirmButton);
    panel.appendChild(hint);
    this.overlay.appendChild(panel);
    mount.appendChild(this.overlay);

    window.addEventListener('keydown', this.onKey);
  }

  get visible(): boolean {
    return this.overlay.style.display !== 'none';
  }

  show(choices: DraftChoice[], onPick: (c: DraftChoice) => void): void {
    this.choices = choices;
    this.onPick = onPick;
    this.selected = 0;
    this.cards = choices.map((c, i) => this.card(c, i));
    this.row.replaceChildren(...this.cards);
    this.highlight();
    this.overlay.style.display = 'grid';
    // Treat any currently-held buttons as "down" so a held A (just used to cast) doesn't
    // instantly pick - the first action needs a fresh press.
    this.prevPad.left = true;
    this.prevPad.right = true;
    this.prevPad.a = true;
    this.startPadPoll();
  }

  hide(): void {
    this.overlay.style.display = 'none';
    this.onPick = null;
    if (this.padRaf !== 0) {
      cancelAnimationFrame(this.padRaf);
      this.padRaf = 0;
    }
  }

  private card(c: DraftChoice, index: number): HTMLDivElement {
    const el = document.createElement('div');
    Object.assign(el.style, {
      width: '200px',
      minHeight: '150px',
      padding: '14px',
      borderRadius: '10px',
      border: `2px solid ${hex(c.color)}`,
      background: 'rgba(20,16,30,0.95)',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      transition: 'transform 0.08s, box-shadow 0.12s, background 0.12s, opacity 0.12s',
      touchAction: 'manipulation',
    });
    el.setAttribute('role', 'button');
    el.onmouseenter = (): void => {
      this.selected = index;
      this.highlight();
    };
    el.onclick = (): void => this.select(index);

    const badge = document.createElement('div');
    badge.textContent = `[${index + 1}]`;
    Object.assign(badge.style, { color: hex(c.color), fontSize: '13px', opacity: '0.8' });

    const name = document.createElement('div');
    name.textContent = c.title;
    Object.assign(name.style, { color: hex(c.color), fontSize: '16px', fontWeight: '700' });

    const desc = document.createElement('div');
    desc.textContent = c.desc;
    Object.assign(desc.style, { fontSize: '13px', lineHeight: '1.4', opacity: '0.9' });

    el.append(badge, name, desc);
    return el;
  }

  // Highlight the selected card (lift + glow + brighten); dim the rest.
  private highlight(): void {
    this.cards.forEach((el, i) => {
      const c = this.choices[i];
      const on = i === this.selected;
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
      el.style.background = on ? 'rgba(48,38,70,0.98)' : 'rgba(20,16,30,0.95)';
      el.style.boxShadow = on ? `0 0 16px ${hex(c.color)}` : 'none';
      el.style.transform = on ? 'translateY(-5px) scale(1.05)' : 'none';
      el.style.opacity = on ? '1' : '0.7';
    });
  }

  private onKey = (e: KeyboardEvent): void => {
    if (!this.visible) return;
    setInputMode('keyboard');
    const n = this.choices.length;
    if (n === 0) return;
    if (e.code === 'ArrowLeft') {
      e.preventDefault();
      this.selected = (this.selected - 1 + n) % n;
      this.highlight();
      return;
    }
    if (e.code === 'ArrowRight') {
      e.preventDefault();
      this.selected = (this.selected + 1) % n;
      this.highlight();
      return;
    }
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      e.preventDefault();
      this.confirm();
      return;
    }
    const map: Record<string, number> = { Digit1: 0, Digit2: 1, Digit3: 2, Digit4: 3 };
    const idx = map[e.code];
    if (idx !== undefined && idx < n) {
      e.preventDefault();
      this.select(idx);
    }
  };

  private startPadPoll(): void {
    const loop = (): void => {
      if (!this.visible) {
        this.padRaf = 0;
        return;
      }
      this.pollPad();
      this.padRaf = requestAnimationFrame(loop);
    };
    this.padRaf = requestAnimationFrame(loop);
  }

  // Gamepad navigation while the draft is open (the sim loop is paused, so Input isn't
  // polling): d-pad or left stick move the selection, A picks.
  private pollPad(): void {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let gp: Gamepad | null = null;
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (p !== null) {
        gp = p;
        break;
      }
    }
    if (gp === null) return;
    const ax = gp.axes[0] ?? 0;
    const left = (gp.buttons[14]?.pressed ?? false) || ax < -0.5;
    const right = (gp.buttons[15]?.pressed ?? false) || ax > 0.5;
    const a = gp.buttons[0]?.pressed ?? false;
    if (left || right || a) setInputMode('gamepad', looksXbox(gp.id));
    const n = this.choices.length;
    if (n > 0) {
      if (left && !this.prevPad.left) {
        this.selected = (this.selected - 1 + n) % n;
        this.highlight();
      } else if (right && !this.prevPad.right) {
        this.selected = (this.selected + 1) % n;
        this.highlight();
      }
      if (a && !this.prevPad.a) this.confirm();
    }
    this.prevPad.left = left;
    this.prevPad.right = right;
    this.prevPad.a = a;
  }

  private select(index: number): void {
    if (index < 0 || index >= this.choices.length) return;
    this.selected = index;
    this.highlight();
  }

  private confirm(): void {
    const cb = this.onPick;
    const choice = this.choices[this.selected];
    if (cb && choice) cb(choice);
  }
}
